import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box, Typography, Button, Tabs, Tab, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, Stack, CircularProgress, Alert, useTheme, alpha,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import { ACCENT } from "./accent";
import { getCatalog, listDrones } from "./api/propulsion";
import { PART_TYPES, PART_META, PART_FIELDS } from "./partSpecs";
import AddPartDialog from "./AddPartDialog";
import AddDroneDialog from "./AddDroneDialog";
import { APP_BASE } from "./paths";
import CollectionToolbar, { FilterChips } from "../../Templates/CollectionToolbar";

// Compact column set per part type: brand, name + the first few spec fields.
const columnsFor = (type) => PART_FIELDS[type].slice(0, 6);

const DRONES_TAB = PART_TYPES.length; // drones sit after the four part tabs

const CatalogPage = () => {
  const theme = useTheme();

  const [catalog, setCatalog] = useState(null);
  const [drones, setDrones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState(0);
  const [addPartOpen, setAddPartOpen] = useState(false);
  const [addDroneOpen, setAddDroneOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [verified, setVerified] = useState("all");

  useEffect(() => {
    Promise.all([getCatalog(), listDrones()])
      .then(([cat, dr]) => {
        setCatalog(cat);
        setDrones(dr);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const isDrones = tab === DRONES_TAB;
  const type = isDrones ? null : PART_TYPES[tab];
  const meta = type ? PART_META[type] : null;
  const rows = catalog && meta ? catalog[meta.catalogKey] : [];
  const columns = useMemo(() => (type ? columnsFor(type) : []), [type]);

  const onPartCreated = (saved) => {
    setCatalog((c) => ({ ...c, [meta.catalogKey]: [saved, ...c[meta.catalogKey]] }));
    setAddPartOpen(false);
  };
  const onDroneCreated = (saved) => {
    setDrones((d) => [saved, ...d]);
    setAddDroneOpen(false);
  };

  const cell = (row, field) => {
    const v = row[field.key];
    if (field.type === "bool") return v ? "Yes" : "—";
    return v === null || v === undefined || v === "" ? "—" : String(v);
  };

  // Search whatever the active tab actually shows, rather than a hand-kept list
  // of fields per part type: a motor is searchable by KV and a battery by cell
  // count without either being named here, and adding a column makes it
  // searchable for free.
  const matches = (text) => {
    const q = query.trim().toLowerCase();
    return !q || text.toLowerCase().includes(q);
  };

  const visibleRows = useMemo(() => {
    return (rows || []).filter((row) => {
      if (verified === "verified" && !row.verified) return false;
      if (verified === "unverified" && row.verified) return false;
      return matches(columns.map((f) => cell(row, f)).join(" "));
    });
    // `matches` and `cell` are re-created each render; the values they close
    // over are all listed, so the memo still recomputes exactly when it should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, query, verified]);

  const visibleDrones = useMemo(
    () =>
      drones.filter((d) =>
        matches(
          [d.brand, d.name, d.drone_class, ...(d.motors || []).map((m) => `${m.brand} ${m.name}`)]
            .filter(Boolean)
            .join(" ")
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drones, query]
  );

  const addLabel = isDrones ? "drone" : meta?.label.toLowerCase();

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Button
        component={RouterLink}
        to={APP_BASE}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ color: theme.palette.text.secondary, textTransform: "none", mb: 2 }}
      >
        Propulsion Calculator
      </Button>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Catalog
          </Typography>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
            The shared library of motors, propellers, ESCs and batteries used to build combinations, plus
            standard drones and the motors that fit them.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => (isDrones ? setAddDroneOpen(true) : setAddPartOpen(true))}
          disabled={!catalog}
          sx={{ backgroundColor: ACCENT, textTransform: "none", "&:hover": { backgroundColor: "#0058A5" } }}
        >
          Add {addLabel}
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mt: 2, mb: 1, "& .MuiTabs-indicator": { backgroundColor: ACCENT } }}
      >
        {PART_TYPES.map((t) => (
          <Tab
            key={t}
            label={PART_META[t].plural}
            sx={{ textTransform: "none", "&.Mui-selected": { color: ACCENT } }}
          />
        ))}
        <Tab label="Drones" sx={{ textTransform: "none", "&.Mui-selected": { color: ACCENT } }} />
      </Tabs>

      {!loading && !error && (
        <Box sx={{ mb: 2 }}>
          <CollectionToolbar
            query={query}
            onQueryChange={setQuery}
            placeholder={isDrones ? "Search drones…" : `Search ${meta.plural.toLowerCase()}…`}
          >
            {/* Parts carry a verified flag; drones do not show one, so the
                filter would be a control that changes nothing. */}
            {!isDrones && (
              <FilterChips
                value={verified}
                onChange={setVerified}
                options={[
                  { value: "verified", label: "Verified" },
                  { value: "unverified", label: "Unverified" },
                ]}
              />
            )}
          </CollectionToolbar>
        </Box>
      )}

      {!loading && !error && (
        <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: theme.palette.text.secondary }}>
          {isDrones
            ? `${visibleDrones.length} of ${drones.length} drones`
            : `${visibleRows.length} of ${(rows || []).length} ${meta.plural.toLowerCase()}`}
        </Typography>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress color="secondary" size={28} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : isDrones ? (
        drones.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4 }}>
            No drones yet. Add one and associate the motors that fit it.
          </Typography>
        ) : visibleDrones.length === 0 ? (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4 }}>
            No drone matches “{query}”. Clear the search to see all {drones.length}.
          </Typography>
        ) : (
          <Box sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 2, overflow: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Brand", "Name", "Class", "# Motors", "Frame (mm)", "AUW (g)", "Motors"].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 600 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleDrones.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell>{d.brand}</TableCell>
                    <TableCell>{d.name}</TableCell>
                    <TableCell>{d.drone_class || "—"}</TableCell>
                    <TableCell>{d.num_motors ?? "—"}</TableCell>
                    <TableCell>{d.frame_size_mm ?? "—"}</TableCell>
                    <TableCell>{d.auw_g ?? "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {(d.motors || []).length === 0
                          ? "—"
                          : d.motors.map((m) => (
                              <Chip key={m.id} size="small" variant="outlined" label={`${m.brand} ${m.name}`} />
                            ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )
      ) : rows.length === 0 ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4 }}>
          No {meta.plural.toLowerCase()} yet. Add one to make it available in the builder.
        </Typography>
      ) : visibleRows.length === 0 ? (
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, py: 4 }}>
          Nothing in {meta.plural.toLowerCase()} matches that. Clear the search or the filter to see all{" "}
          {rows.length}.
        </Typography>
      ) : (
        <Box sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.6)}`, borderRadius: 2, overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((f) => (
                  <TableCell key={f.key} sx={{ fontWeight: 600 }}>{f.label}</TableCell>
                ))}
                <TableCell sx={{ fontWeight: 600 }}>Verified</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => (
                <TableRow key={row.id} hover>
                  {columns.map((f) => (
                    <TableCell key={f.key}>{cell(row, f)}</TableCell>
                  ))}
                  <TableCell>
                    {row.verified ? (
                      <Chip size="small" label="Verified"
                        sx={{ backgroundColor: alpha("#2e7d32", 0.15), color: "#2e7d32", fontWeight: 600 }} />
                    ) : (
                      <Chip size="small" label="Unverified" variant="outlined" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {meta && (
        <AddPartDialog open={addPartOpen} type={type} onClose={() => setAddPartOpen(false)} onCreated={onPartCreated} />
      )}
      <AddDroneDialog
        open={addDroneOpen}
        motors={catalog?.motors || []}
        onClose={() => setAddDroneOpen(false)}
        onCreated={onDroneCreated}
      />
    </Box>
  );
};

export default CatalogPage;
